import { Card, CardContent, Grid, Typography, useTheme } from '@mui/material';
import ProgressCards, { ProgressItem } from './ProgressCards';
import { SummedReport } from '../../../models/ReportData';
import { ProductTypeEnum } from '../../../models/PricebookData';
import { Spacer } from '../../../components';
import { HelpOutline } from '@mui/icons-material';
import Tippy from '@tippyjs/react';
import { ReactNode, useEffect, useState } from 'react';
import { progressTracker3Nickels, progressTrackerBundled } from './Progress3nickels';
import { AvgProgress3nuHelp, progressTracker3NU } from './Progress3nu';
import { progressTrackerGuidedChoice } from './ProgressGc';
import { formatNumber } from '../../../helpers/utilityFunctions';
import { Bold } from '../../../components/Bold';

interface CalculatedProgressData {
  title: string;
  totalUsers: number;
  notStarted: number;
  inProgress: ProgressItem[];
  completed: number;
  avgProgress?: number;
  helpText: string;
  helpContent: ReactNode;
}

export type ProgressCalculator = (summedReport: SummedReport) => CalculatedProgressData;

const progressCalculators: Record<string, ProgressCalculator> = {
  [ProductTypeEnum.NickelsU]: progressTracker3NU,
  [ProductTypeEnum.Nickels]: progressTracker3Nickels,
  [ProductTypeEnum.Bundled]: progressTrackerBundled,
  [ProductTypeEnum.GuidedChoice]: progressTrackerGuidedChoice,
};

interface ReportProgressTrackerProps {
  summedReport: SummedReport;
  productType: ProductTypeEnum;
  guidedChoiceOnly?: boolean;
}

const ReportProgressTracker: React.FC<ReportProgressTrackerProps> = ({
  summedReport,
  productType,
  guidedChoiceOnly,
}) => {
  const theme = useTheme();
  const [data, setData] = useState<CalculatedProgressData | null>(null);
  const gcOnly = guidedChoiceOnly && productType === ProductTypeEnum.GuidedChoice;
  const is3nu = productType === ProductTypeEnum.NickelsU;
  const is3n = productType === ProductTypeEnum.Nickels;
  const isBundled = productType === ProductTypeEnum.Bundled;
  const isGc = productType === ProductTypeEnum.GuidedChoice;

  useEffect(() => {
    if (productType) {
      const calculator = progressCalculators[productType];

      if (calculator) {
        const calculatedData = calculator(summedReport);
        setData(calculatedData);
      }
    }
  }, [summedReport, productType]);

  if (!data) {
    return null;
  }

  const {
    title,
    helpContent,
    helpText,
    notStarted,
    inProgress,
    completed,
    totalUsers,
    avgProgress,
  } = data;

  const userCountHeader = [
    <Grid display='inherit'>
      <Typography variant='p16' color='secondary.main'>
        {helpText}
      </Typography>
      {helpContent && (
        <>
          <Spacer width='xxxs' />
          <Tippy placement='right' content={helpContent} theme='tippy-rounded'>
            <HelpOutline style={{ color: theme.palette.info.main }} />
          </Tippy>
        </>
      )}
    </Grid>,

    <Spacer width='xxs' />,

    <Typography variant='p24Bold' color='secondary.main'>
      {formatNumber(totalUsers)}
    </Typography>,
  ];

  const statusColumns = [
    <Grid size='grow' whiteSpace='nowrap'>
      <Typography variant='p16' color='secondary.main'>
        {isGc ? 'Incomplete' : 'Not started'}
      </Typography>
      <Spacer height='xxs' />
      <ProgressCards
        outerStyle={{ minHeight: '80px' }}
        valueStyle={{ height: '100%' }}
        cards={[{ value: notStarted }]}
      />
    </Grid>,

    inProgress.length > 0 ? (
      <Grid justifyContent='flex-start' size={8.5}>
        <Grid sx={{ px: 2 }} display='flex' flexDirection='column' flex={1}>
          <Typography variant='p16' color='secondary.main'>
            In progress
          </Typography>
          <Spacer height='xxs' />
          <ProgressCards cards={inProgress} outerStyle={{ minHeight: '80px' }} />
        </Grid>
      </Grid>
    ) : (
      <Grid size={8.5} />
    ),

    <Grid size='grow' whiteSpace='nowrap'>
      <Typography variant='p16' color='secondary.main'>
        Completed
      </Typography>
      <Spacer height='xxs' />
      <ProgressCards
        outerStyle={{ minHeight: '80px' }}
        valueStyle={!is3n && !isBundled ? { height: '100%' } : undefined}
        boldValue
        cards={[
          {
            label: is3n || isBundled ? 'Ran advice for the first time' : undefined,
            value: completed,
          },
        ]}
      />
    </Grid>,
  ];

  return (
    <Grid pb={3} height='100%'>
      <Grid
        flexDirection={gcOnly ? 'column' : 'row'}
        display='flex'
        justifyContent='space-between'
        height='100%'>
        <Typography variant='p24' color='secondary.main'>
          {title}
        </Typography>
        <Spacer height='xxxs' />

        <Grid display='flex' alignItems='center'>
          {gcOnly ? userCountHeader.reverse() : userCountHeader}
        </Grid>
      </Grid>

      <Spacer height='xxs' />

      <Card variant='ghost' color='primary'>
        <CardContent>
          <Grid container flexDirection='row' flexWrap='nowrap' justifyContent='space-between'>
            {gcOnly ? statusColumns.reverse() : statusColumns}
          </Grid>
        </CardContent>
      </Card>

      {is3nu && (
        <Grid display='flex' flexDirection='row' mt={1} ml='15px' alignItems='center'>
          <Typography variant='p16' color='secondary.main'>
            average progress
          </Typography>
          <Spacer width='xxs' />
          <Typography variant='p16' color='secondary.main'>
            <Bold>{avgProgress?.toFixed(2)}</Bold>%
          </Typography>
          <Spacer width='xxxs' />
          <Tippy placement='right' content={<AvgProgress3nuHelp />} theme='tippy-rounded'>
            <HelpOutline style={{ color: theme.palette.info.main }} />
          </Tippy>
        </Grid>
      )}
    </Grid>
  );
};

export default ReportProgressTracker;
